import { forwardRef } from 'react';
import { type VariantProps, cva } from 'class-variance-authority';

import classNames from '@/utils/classnames';

export const cardVariants = cva('border border-line bg-surface-2', {
  variants: {
    radius: {
      lg: 'rounded-lg',
      xl: 'rounded-xl',
      '2xl': 'rounded-2xl',
    },
    padding: {
      none: '',
      sm: 'p-4',
      md: 'p-6',
    },
    clickable: {
      true: 'cursor-pointer transition-colors hover:bg-surface-3',
      false: '',
    },
  },
  defaultVariants: {
    radius: 'xl',
    padding: 'md',
    clickable: false,
  },
});

export interface CardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'className'>, VariantProps<typeof cardVariants> {
  className?: string;
}

/**
 * Surface container. Use the default `padding` for simple cards; pass
 * `padding="none"` when composing with {@link CardHeader}/{@link CardContent}.
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ radius, padding, clickable, className, ...props }, ref) => (
    <div ref={ref} className={classNames(cardVariants({ radius, padding, clickable }), className)} {...props} />
  ),
);
Card.displayName = 'Card';

export function CardHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={classNames(
        'flex items-center justify-between gap-2 border-b border-line-subtle px-5 py-3.5',
        className,
      )}
      {...props}
    />
  );
}

export function CardTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={classNames('card-heading', className)} {...props} />;
}

export function CardContent({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={classNames('p-5', className)} {...props} />;
}
